import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

const PhotoUploadSection = ({ events }) => {
  const [photos, setPhotos] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileRef = useRef();

  const loadPhotos = () => {
    api.get('/photos').then(r => setPhotos(r.data)).catch(() => {});
  };

  useEffect(() => { loadPhotos(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedEvent = events.find(e => e.id === selectedEventId);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    let done = 0;
    for (const file of files) {
      try {
        setUploadProgress(`Uploading ${file.name} (${done + 1}/${files.length})...`);
        const { data } = await api.post('/photos/upload-url', {
          filename: file.name,
          content_type: file.type,
          event_id: selectedEventId || null,
          event_title: selectedEvent?.title || null,
          caption: caption || null,
        });
        const form = new FormData();
        Object.entries(data.upload_fields).forEach(([k, v]) => form.append(k, v));
        form.append('file', file);
        await fetch(data.upload_url, { method: 'POST', body: form });
        done++;
      } catch {
        // continue with remaining files
      }
    }
    setUploading(false);
    setUploadProgress('');
    setCaption('');
    fileRef.current.value = '';
    loadPhotos();
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api.delete(`/photos/${photoId}`);
      loadPhotos();
    } catch {}
  };

  const photosByEvent = photos.reduce((acc, p) => {
    const key = p.eventTitle || 'Unassigned';
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  return (
    <div className="admin-photo-section">
      <h3 className="admin-section-title">Event Photos</h3>

      <div className="photo-upload-form">
        <select
          className="form-input"
          value={selectedEventId}
          onChange={e => setSelectedEventId(e.target.value)}
        >
          <option value="">— Select event (optional) —</option>
          {events.map(ev => (
            <option key={ev.id} value={ev.id}>{ev.title} ({ev.date})</option>
          ))}
        </select>

        <input
          type="text"
          className="form-input"
          placeholder="Caption (optional)"
          value={caption}
          onChange={e => setCaption(e.target.value)}
        />

        <label className="photo-upload-btn">
          {uploading ? uploadProgress : '+ Upload Photos'}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      </div>

      {Object.entries(photosByEvent).map(([eventTitle, eventPhotos]) => (
        <div key={eventTitle} className="photo-group">
          <h4 className="photo-group-title">{eventTitle}</h4>
          <div className="photo-admin-grid">
            {eventPhotos.map(p => (
              <div key={p.id} className="photo-admin-thumb">
                <img src={p.url} alt={p.caption || p.filename} />
                {p.caption && <div className="photo-admin-caption">{p.caption}</div>}
                <button
                  className="photo-delete-btn"
                  onClick={() => handleDelete(p.id)}
                >✕</button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {photos.length === 0 && (
        <p style={{ color: '#999', marginTop: '12px' }}>No photos uploaded yet.</p>
      )}
    </div>
  );
};

export default PhotoUploadSection;
